import { Button, FlashMessage, Modal, ReadMoreLess, RichTextEditor } from "../../common";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import { updateCandidateProfessionalPassionRequest } from "../../../reducers/candidate/candidate.reducer";
import EditIcon from "../icons/edit-icon.svg";
import { stripHTMLTags } from "../../../utils/helper";

interface ProfessionalPassionsProps {
  professional_passions: {
    early_career?: string;
    mid_career?: string;
    recent_life?: string;
  };
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}
function ProfessionalPassions({
  professional_passions,
  isPublicPage,
  openRequest,
  onOpenHandled,
}: ProfessionalPassionsProps) {
  const [professionalPassion, setProfessionalPassion] = useState(false);
  const [localData, setLocalData] = useState(professional_passions);
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ProfessionalPassionsProps>({
    defaultValues: {
      professional_passions: professional_passions || {},
    },
  });

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );

  const openProfessionalModal = () => {
    if (isPublicPage) return;
    const updatedProfessionalPassions = watch("professional_passions");
    reset({ professional_passions: updatedProfessionalPassions });
    setProfessionalPassion(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openProfessionalModal();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closeEducationModal = () => {
    const updatedProfessionalPassions = watch("professional_passions");
    reset({ professional_passions: updatedProfessionalPassions });
    setProfessionalPassion(false);
  };

  useEffect(() => {
    reset({ professional_passions });
    setLocalData(professional_passions);
  }, [professional_passions, reset]);

  const onSubmit = (data: ProfessionalPassionsProps) => {
    const payload = data.professional_passions;

    dispatch(updateCandidateProfessionalPassionRequest({ payload }));
  };

  useEffect(() => {
    if (candidateViewFlag === "professional_passion_success") {
      FlashMessage("Professional passions updated successfully");
      const updatedData = watch("professional_passions");
      setLocalData(updatedData);
      reset({ professional_passions: updatedData });
      setProfessionalPassion(false);
    }
    if (candidateViewFlag === "professional_passion_failed") {
      FlashMessage("Failed to update professional passions", "error");
    }

    // eslint-disable-next-line
  }, [candidateViewFlag]);

  return (
    <div className="accordion" id="profile-passion">
      <div className="accordion-head">
        <b>Professional Passion</b>
        {!isPublicPage &&
          (!_.isEmpty(localData?.recent_life) ||
          !_.isEmpty(localData?.mid_career) ||
          !_.isEmpty(localData?.early_career) ? (
            <div className="edit-icon" onClick={openProfessionalModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openProfessionalModal}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localData?.recent_life) &&
        !_.isEmpty(localData?.mid_career) &&
        !_.isEmpty(localData?.early_career) && (
          <div className="accordion-body">
            <div className={`form-wrapper ${isPublicPage ? 'public-page' : ''}`}>
              {!_.isEmpty(localData?.recent_life) && (
                <div className="form-group">
                  <label className="form-label">Recent Career</label>
                  <div className="bordered-content wysiwyg-data">
                    <ReadMoreLess text={_.get(localData, "recent_life", "")} textLimit={300} />
                  </div>
                </div>
              )}
              {!_.isEmpty(localData?.mid_career) && (
                <div className="form-group">
                  <label className="form-label">Mid Journey</label>
                  <div className="bordered-content wysiwyg-data">
                    <ReadMoreLess text={_.get(localData, "mid_career", "")} textLimit={300} />
                  </div>
                </div>
              )}
              {!_.isEmpty(localData?.early_career) && (
                <div className="form-group">
                  <label className="form-label">Earlier Years</label>
                  <div className="bordered-content wysiwyg-data">
                    <ReadMoreLess text={_.get(localData, "early_career", "")} textLimit={300} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      {!isPublicPage && (
        <Modal
          className="candidate-profile-modal project-modal certificate-modal"
          isOpen={professionalPassion}
          onClose={closeEducationModal}
          title={
            !_.isEmpty(localData?.recent_life) ||
            !_.isEmpty(localData?.mid_career) ||
            !_.isEmpty(localData?.early_career)
              ? "Edit Professional Passion"
              : "Professional Passion"
          }
          visible={professionalPassion}
          size="large"
        >
        <div className="modal-body-wrapper">
          <form className="form-wrapper" onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group ">
              <RichTextEditor
                name="professional_passions.recent_life"
                defaultValue={professional_passions?.recent_life}
                externalLabel={{
                  label: "Recent Career",
                  className: "required",
                }}
                placeholder="Description"
                validationObj={errors}
                control={control}
                rules={{
                  validate: {
                    validLength: (value: any) => {
                      const tmpText = stripHTMLTags(value || "");
                      if (tmpText.length > 1000) {
                        return "Can not exceed more then 1000 characters";
                      }
                      return true;
                    },
                  },
                  required: {
                    value: true,
                    message: "Please enter description",
                  },
                }}
                showCount
                maxLimit={1000}
                counterAlign="right"
              />
            </div>
            <div className="form-group ">
              <RichTextEditor
                name="professional_passions.mid_career"
                defaultValue={professional_passions?.mid_career}
                externalLabel={{
                  label: "Mid Journey",
                  className: "required",
                }}
                placeholder="Description"
                validationObj={errors}
                control={control}
                rules={{
                  validate: {
                    validLength: (value: any) => {
                      const tmpText = stripHTMLTags(value || "");
                      if (tmpText.length > 1000) {
                        return `Can not be more then 1000 characters`;
                      } else if (tmpText.length < 2) {
                        return `Can not be less then 2 characters`;
                      } else {
                        return true;
                      }
                    },
                  }, 
                  required: {
                    value: true,
                    message: "Please enter description",
                  },
                }}
                showCount
                maxLimit={1000}
                counterAlign="right"
              />
            </div>
            <div className="form-group ">
              <RichTextEditor
                name="professional_passions.early_career"
                defaultValue={professional_passions?.early_career}
                externalLabel={{
                  label: "Earlier Years",
                  className: "required",
                }}
                placeholder="Description"
                validationObj={errors}
                control={control}
                rules={{
                  validate: {
                    validLength: (value: any) => {
                      const tmpText = stripHTMLTags(value || "");
                        if (tmpText.length > 1000) {
                          return `Can not be more then 1000 characters`;
                        } else if (tmpText.length < 2) {
                          return `Can not be less then 2 characters`;
                        } else {
                          return true;
                        }
                    },
                  },
                  required: {
                    value: true,
                    message: "Please enter description",
                  },
                }}
                showCount
                maxLimit={1000}
                counterAlign="right"
              />
            </div>
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={() => {
                  setProfessionalPassion(false);
                }}
                color="none"
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="save-btn"
                type="submit"
              >
                Save
              </Button>
            </div>
          </form>
        </div>
        </Modal>
      )}
    </div>
  );
}

export default ProfessionalPassions;
